import { useMemo, useState } from 'react'
import { BadgeCheck, ChevronsUpDown, Circle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { UserAvatar, UserAvatarStack } from '@/components/composites/user-avatar'
import { cn } from '@/lib/utils'
import type { Person } from './pickerData'
import { normalizeHumanText } from '@/lib/text-normalization'

interface AssigneePickerProps {
  people: Person[]
  selectedIds: string[]
  pointPersonId: string
  onChange: (selectedIds: string[], pointPersonId: string) => void
  disabled?: boolean
  invalid?: boolean
  max?: number
  meId?: string
}

/**
 * Multi-assignee picker with one explicit owner.
 *
 * The owner is always also an assignee. Existing single-assignee work therefore maps
 * without a migration: its historical ownerId is selected and wears the checkmark.
 */
export function AssigneePicker({
  people,
  selectedIds,
  pointPersonId,
  onChange,
  disabled = false,
  invalid = false,
  max = 10,
  meId,
}: AssigneePickerProps) {
  const [query, setQuery] = useState('')
  const selected = useMemo(() => {
    const ids = new Set(selectedIds)
    if (pointPersonId) ids.add(pointPersonId)
    return [...ids]
  }, [selectedIds, pointPersonId])
  const selectedPeople = selected
    .map((id) => people.find((person) => person.id === id))
    .filter((person): person is Person => Boolean(person))
    .sort((a, b) => Number(b.id === pointPersonId) - Number(a.id === pointPersonId))
  const filtered = people
    .filter((person) => normalizeHumanText(person.displayName).includes(normalizeHumanText(query)))
    .sort((a, b) => {
      // When re-editing an item, the people already assigned stay at the top rather than
      // disappearing into the alphabet. Owner first, then the other current people.
      const aRank = a.id === pointPersonId ? 0 : selected.includes(a.id) ? 1 : 2
      const bRank = b.id === pointPersonId ? 0 : selected.includes(b.id) ? 1 : 2
      return aRank - bRank || a.displayName.localeCompare(b.displayName)
    })

  const commit = (ids: string[], point: string) => {
    const unique = [...new Set(ids)].slice(0, max)
    const nextPoint = point && unique.includes(point) ? point : (unique[0] ?? '')
    onChange(unique, nextPoint)
  }

  const toggle = (id: string, checked: boolean) => {
    if (checked) {
      if (selected.includes(id) || selected.length >= max) return
      commit([...selected, id], pointPersonId || id)
      return
    }

    // A Work Item must always have one owner. Removing the checked person promotes the
    // next selected assignee; removing the last remaining assignee is intentionally refused.
    if (selected.length <= 1) return
    const next = selected.filter((candidate) => candidate !== id)
    commit(next, id === pointPersonId ? next[0] ?? '' : pointPersonId)
  }

  const markPointPerson = (id: string) => {
    const ids = selected.includes(id) ? selected : [...selected, id]
    commit(ids, id)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-invalid={invalid}
          className={cn(
            'h-auto min-h-9 w-full justify-between px-3 py-1.5 font-normal',
            invalid && 'border-danger',
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selectedPeople.length > 0 ? (
              <UserAvatarStack people={selectedPeople} max={3} size="xs" />
            ) : null}
            <span className={cn('min-w-0 truncate text-left text-sm', selectedPeople.length === 0 && 'text-text-muted')}>
              {selectedPeople.length === 0 ? (
                'Choose assignees'
              ) : (
                <>
                  <span className="block truncate">
                    {selectedPeople.map((person) => person.displayName).join(', ')}
                  </span>
                  <span className="block truncate text-tiny text-text-muted">
                    Owner: {selectedPeople.find((person) => person.id === pointPersonId)?.displayName ?? selectedPeople[0]?.displayName}
                  </span>
                </>
              )}
            </span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-text-muted" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(26rem,var(--radix-popover-trigger-width))] p-2">
        {selectedPeople.length > 0 ? (
          <div className="mb-2 rounded-md bg-muted/60 px-2.5 py-2">
            <p className="microlabel mb-1">Currently assigned</p>
            <p className="text-xs text-text-secondary">
              {selectedPeople.map((person) =>
                person.id === pointPersonId ? `${person.displayName} (owner)` : person.displayName,
              ).join(' · ')}
            </p>
          </div>
        ) : null}
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search people…"
          className="mb-2"
        />
        <div className="max-h-72 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-2 py-4 text-center text-tiny text-text-muted">No matching people.</p>
          ) : (
            filtered.map((person) => {
              const checked = selected.includes(person.id)
              const point = pointPersonId === person.id
              const atLimit = !checked && selected.length >= max
              return (
                <div
                  key={person.id}
                  className="flex items-center gap-2 rounded-md px-2 py-2 hover:bg-muted"
                >
                  <Checkbox
                    checked={checked}
                    disabled={atLimit || (checked && selected.length === 1)}
                    onCheckedChange={(next) => toggle(person.id, next === true)}
                    aria-label={`Assign ${person.displayName}`}
                  />
                  <UserAvatar person={person} size="xs" />
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left text-sm"
                    onClick={() => toggle(person.id, !checked)}
                    disabled={atLimit || (checked && selected.length === 1)}
                  >
                    {person.displayName}
                    {person.id === meId ? <span className="ml-1 text-text-muted">(me)</span> : null}
                  </button>
                  <button
                    type="button"
                    onClick={() => markPointPerson(person.id)}
                    disabled={disabled || (!checked && selected.length >= max)}
                    className={cn(
                      'flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-tiny transition-colors',
                      point ? 'bg-success-soft font-medium text-success' : 'text-text-muted hover:bg-muted',
                    )}
                    title={point ? 'Owner' : 'Make owner'}
                    aria-label={point ? `${person.displayName} is the owner` : `Make ${person.displayName} owner`}
                  >
                    {point ? <BadgeCheck className="size-4" /> : <Circle className="size-4" />}
                    <span className="hidden sm:inline">Owner</span>
                  </button>
                </div>
              )
            })
          )}
        </div>
        <p className="border-t border-border pt-2 text-tiny text-text-muted">
          Up to {max} people. Exactly one checked owner is accountable for the item.
        </p>
      </PopoverContent>
    </Popover>
  )
}
